<template>
  <div class="form_field">
    <div v-if="field.type === fieldTypes.TEXT_DISPLAY">
      <div class="text-sm font-medium mb-1">{{ field.label }}</div>
      <div v-if="field.description" class="text-sm secondary_color whitespace-pre-wrap">
        {{ field.description }}
      </div>
    </div>
    
    <div v-else-if="field.type === fieldTypes.IMAGE">
      <div class="text-sm font-medium mb-2">{{ field.label }}</div>
      <div 
        v-if="field.imageUrl"
        :style="getImageStyles(field)"
        class="image_container"
      >
        <img 
          :src="field.imageUrl" 
          :alt="field.altText || field.label"
          class="max-w-full h-auto rounded"
        />
      </div>
      <div
        v-else
        class="w-full h-32 border-2 border-dashed border_8 rounded flex items-center justify-center"
      >
        <span class="text-xs secondary_color">Image placeholder</span>
      </div>
    </div>

    <div v-else-if="field.type === fieldTypes.FORMULA" class="read-only-field">
      <label class="block text-sm font-medium mb-2">
        {{ field.label }}
      </label>
      <div class="readonly-value-display">
        <span class="text-sm secondary_color">
          {{ modelValue ?? '--' }}
        </span>
      </div>
    </div>

    <div v-else-if="field.type === fieldTypes.PROPERTY_SYNC" class="read-only-field">
      <label class="block text-sm font-medium mb-2">
        {{ field.label }}
      </label>
      <div class="readonly-value-display">
        <span class="text-sm secondary_color">
          {{ modelValue ?? '--' }}
        </span>
      </div>
    </div>

    <div v-else-if="field.type === fieldTypes.SELECT_TIMESTAMP" class="read-only-field">
      <label class="block text-sm font-medium mb-2">
        {{ field.label }}
      </label>
      <div class="readonly-value-display">
        <span class="text-sm secondary_color">
          {{ modelValue ?? '--' }}
        </span>
      </div>
    </div>

    <div v-else-if="field.type === fieldTypes.RELATION_RECORD" class="read-only-field">
      <label class="block text-sm font-medium mb-2">
        {{ field.label }}
      </label>
      <div class="readonly-value-display">
        <template v-if="parsedRelationRecords && parsedRelationRecords.length > 0">
          <div class="flex flex-wrap gap-2">
            <div
              v-for="(record, index) in parsedRelationRecords"
              :key="index"
              class="relation-record-chip"
              @click="navigateToRecord(record)"
            >
              <div
                class="relation-record-avatar"
                :style="{ backgroundColor: getAvatarColor(record.record_display_name) }"
              >
                {{ getInitial(record.record_display_name) }}
              </div>
              <span class="relation-record-name">
                {{ record.record_display_name || 'View Record' }}
              </span>
            </div>
          </div>
        </template>
        <span v-else class="text-sm secondary_color">
          Related records
        </span>
      </div>
    </div>

    <div v-else>
      <label v-if="field.type !== fieldTypes.MEETING_LOG && field.type !== fieldTypes.ENTITY" class="block text-sm font-medium mb-2">
        {{ field.label }}
        <span v-if="field.required" class="text-red-500 ml-1">*</span>
      </label>
      <div v-if="field.id === 'description' || field.fieldName === 'description'" :class="{ 'field-error': error }">
        <a-textarea
          ref="textareaRef"
          v-model:value="modelValue"
          :placeholder="field.placeholder || 'Enter a description'"
          :disabled="disabled"
          :rows="4"
          class="textarea_large"
        />
        <div v-if="error && errorMessage" class="error_message mt-1">
          {{ errorMessage }}
        </div>
      </div>

      <CustomInput
        v-else-if="[fieldTypes.TEXT, fieldTypes.EMAIL].includes(field.type) && !isTagsField"
        ref="textInputRef"
        v-model="modelValue"
        format="text"
        :placeholder="field.placeholder || 'Enter ' + field.label.toLowerCase()"
        :disabled="disabled"
        :isError="error"
        :errorMessage="errorMessage"
        @keydown.enter="handleEnterKey"
      />

      <div v-else-if="isTagsField" :class="{ 'field-error': error }">
        <a-select
          ref="tagsSelectRef"
          v-model:value="modelValue"
          mode="tags"
          :token-separators="[',']"
          :placeholder="(field.placeholder && field.placeholder.trim()) || 'Enter tags'"
          :disabled="disabled"
          :options="tagOptions"
          show-search
          allow-clear
          :filter-option="filterObjectOption"
          class="w-full"
          style="width: 100%"
        >
        </a-select>
        <div v-if="error && errorMessage" class="error_message mt-1">
          {{ errorMessage }}
        </div>
      </div>

      <div v-else-if="field.type === fieldTypes.SINGLE_SELECT" :class="{ 'field-error': error }">
        <a-select
          ref="selectRef"
          v-model:value="modelValue"
          :placeholder="(field.placeholder && field.placeholder.trim()) || 'Select an option'"
          :disabled="disabled"
          show-search
          allow-clear
          :filter-option="filterObjectOption"
          :options="field.options || []"
          class="w-full"
        >
          <template #suffixIcon>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_white_arrow"
              class="size-4"
            />
          </template>
        </a-select>
        <div v-if="error && errorMessage" class="error_message mt-1">
          {{ errorMessage }}
        </div>
      </div>
      
      <div v-else-if="field.type === fieldTypes.MULTI_SELECT" :class="{ 'field-error': error }">
        <a-select
          ref="multiSelectRef"
          v-model:value="modelValue"
          mode="multiple"
          :placeholder="(field.placeholder && field.placeholder.trim()) || 'Select options'"
          :disabled="disabled"
          show-search
          allow-clear
          :filter-option="filterObjectOption"
          :options="field.options || []"
          class="w-full"
        >
          <template #suffixIcon>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_white_arrow"
              class="size-4"
            />
          </template>
        </a-select>
        <div v-if="error && errorMessage" class="error_message mt-1">
          {{ errorMessage }}
        </div>
      </div>
      
      <div v-else-if="field.type === fieldTypes.DATE" :class="{ 'field-error': error }">
        <a-date-picker
          ref="datePickerRef"
          v-model:value="modelValue"
          :placeholder="field.placeholder || 'Select date'"
          :disabled="disabled"
          allow-clear
          class="w-full"
        />
        <div v-if="error && errorMessage" class="error_message mt-1">
          {{ errorMessage }}
        </div>
      </div>

      <div v-else-if="field.type === fieldTypes.DATETIME" :class="{ 'field-error': error }">
        <a-date-picker
          ref="dateTimePickerRef"
          v-model:value="modelValue"
          show-time
          :placeholder="field.placeholder || 'Select date and time'"
          :disabled="disabled"
          allow-clear
          class="w-full"
        />
        <div v-if="error && errorMessage" class="error_message mt-1">
          {{ errorMessage }}
        </div>
      </div>

      <div v-else-if="field.type === fieldTypes.TIME" :class="{ 'field-error': error }">
        <a-time-picker
          v-model:value="modelValue"
          :placeholder="field.placeholder || 'Select time'"
          :disabled="disabled"
          format="HH:mm"
          allow-clear
          class="w-full"
        />
        <div v-if="error && errorMessage" class="error_message mt-1">
          {{ errorMessage }}
        </div>
      </div>

      <div v-else-if="field.type === fieldTypes.MEETING_LOG" class="meeting-log-action">
        <div class="flex flex-col gap-4">
          <div class="form_col">
            <label class="block text-[13px] font-medium mb-1" style="color: var(--text-color);">Attendees <span class="text-red-500">*</span></label>
            <a-select
              v-model:value="meetingLogValues.attendees"
              mode="multiple"
              placeholder="Select attendees"
              :disabled="disabled"
              show-search
              :allow-clear="false"
              :filter-option="false"
              :options="attendeeOptions"
              class="w-full"
              @search="searchAttendees"
              @dropdown-visible-change="handleAttendeesDropdownChange"
              @deselect="handleAttendeeDeselect"
            />
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="form_col">
              <label class="block text-[13px] font-medium mb-1" style="color: var(--text-color);">Outcome</label>
              <a-select
                v-model:value="meetingLogValues.outcome"
                placeholder="Select outcome"
                :disabled="disabled"
                show-search
                allow-clear
                :filter-option="filterObjectOption"
                class="w-full"
              >
                <a-select-option
                  v-for="(outcome, idx) in meetingOutcomeOpts"
                  :key="idx"
                  :value="outcome"
                >{{ outcome }}</a-select-option>
              </a-select>
            </div>
            <div class="form_col">
              <label class="block text-[13px] font-medium mb-1" style="color: var(--text-color);">Disposition</label>
              <MeetingDispositionSelect
                v-model="meetingLogValues.disposition"
                select-class="w-full"
                :disabled="disabled"
              />
            </div>
          </div>

          <div class="form_col">
            <label class="block text-[13px] font-medium mb-1" style="color: var(--text-color);">Date <span class="text-red-500">*</span></label>
            <a-date-picker
              v-model:value="meetingLogValues.date"
              placeholder="Select date"
              :disabled="disabled"
              allow-clear
              class="w-full"
            />
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="form_col">
              <label class="block text-[13px] font-medium mb-1" style="color: var(--text-color);">Start Time <span class="text-red-500">*</span></label>
              <a-select
                v-model:value="meetingLogValues.start_time"
                placeholder="Select start time"
                :disabled="disabled"
                show-search
                :filter-option="filterObjectOption"
                :options="timeOptions"
                class="w-full"
              />
            </div>
            <div class="form_col">
              <label class="block text-[13px] font-medium mb-1" style="color: var(--text-color);">End Time <span class="text-red-500">*</span></label>
              <a-select
                v-model:value="meetingLogValues.end_time"
                placeholder="Select end time"
                :disabled="disabled"
                show-search
                :filter-option="filterObjectOption"
                :options="timeOptions"
                class="w-full"
              />
            </div>
          </div>

          <div class="form_col">
            <label class="block text-[13px] font-medium mb-1" style="color: var(--text-color);">Description</label>
            <a-textarea
              v-model:value="meetingLogValues.description"
              placeholder="Enter meeting description"
              :disabled="disabled"
              :rows="3"
              class="textarea_large"
            />
          </div>

          <div class="form_col">
            <label class="block text-[13px] font-medium mb-1" style="color: var(--text-color);">Attachments</label>
            <a-upload
              :file-list="meetingAttachmentFileList"
              :disabled="disabled"
              :before-upload="() => false"
              @change="handleMeetingAttachmentChange"
            >
              <a-button :disabled="disabled" size="small">Upload File</a-button>
            </a-upload>
          </div>
        </div>
      </div>

      <div v-else-if="field.type === fieldTypes.TEXTAREA || field.type === 'add-note'" :class="{ 'field-error': error }">
        <a-textarea
          ref="textareaRef"
          v-model:value="modelValue"
          :placeholder="field.placeholder || 'Enter text'"
          :disabled="disabled"
          :rows="4"
          class="textarea_large"
        />
        <div v-if="error && errorMessage" class="error_message mt-1">
          {{ errorMessage }}
        </div>
      </div>

      <CustomInput
        v-else-if="field.type === fieldTypes.NUMBER"
        ref="numberInputRef"
        v-model="modelValue"
        format="number"
        :placeholder="field.placeholder || 'Enter number'"
        :disabled="disabled"
        :isError="error"
        :errorMessage="errorMessage"
        @keydown.enter="handleEnterKey"
      />

      <CustomInput
        v-else-if="field.type === fieldTypes.URL || field.type === fieldTypes.DOMAIN"
        ref="urlInputRef"
        v-model="modelValue"
        format="text"
        :placeholder="field.placeholder || (field.type === fieldTypes.URL ? 'Enter URL' : 'Enter domain')"
        :disabled="disabled"
        :isError="error"
        :errorMessage="errorMessage"
        @keydown.enter="handleEnterKey"
      />

      <div v-else-if="field.type === fieldTypes.PHONE" :class="{ 'field-error': error }">
        <vue-tel-input
          ref="phoneInputRef"
          v-model="modelValue"
          :defaultCountry="defaultCountryCode"
          :dropdownOptions="{
            showFlags: true,
            showDialCodeInList: true,
            showSearchBox: true,
            searchBoxPlaceholder: 'Search country..',
          }"
          mode="international"
          :disabled="disabled"
          :placeholder="field.placeholder || 'Enter phone number'"
        />
        <div v-if="error && errorMessage" class="error_message mt-1">
          {{ errorMessage }}
        </div>
      </div>

      <div v-else-if="field.type === fieldTypes.USER" :class="{ 'field-error': error }">
        <a-select
          ref="userSelectRef"
          v-model:value="modelValue"
          :placeholder="(field.placeholder && field.placeholder.trim()) || 'Select user'"
          :disabled="disabled"
          :loading="loadingUsers"
          show-search
          allow-clear
          :filter-option="filterObjectOption"
          :options="userOptions"
          class="w-full"
        >
          <template #suffixIcon>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_white_arrow"
              class="size-4"
            />
          </template>
        </a-select>
        <div v-if="error && errorMessage" class="error_message mt-1">
          {{ errorMessage }}
        </div>
      </div>

      <div v-else-if="field.type === fieldTypes.FILE" :class="{ 'field-error': error }">
        <a-upload
          ref="uploadRef"
          v-model:file-list="modelValue"
          :maxCount="1"
          :disabled="disabled"
        >
          <a-button :disabled="disabled">
            <span>{{ field.placeholder || 'Upload File' }}</span>
          </a-button>
        </a-upload>
        <div v-if="error && errorMessage" class="error_message mt-1">
          {{ errorMessage }}
        </div>
      </div>

      <div v-else-if="field.type === fieldTypes.ENTITY" class="entity-field">
        <label class="block text-sm font-medium mb-2">
          {{ field.label }}
          <span v-if="field.required" class="text-red-500 ml-1">*</span>
        </label>
        <a-select
          v-model:value="modelValue"
          :placeholder="field.placeholder || 'Search and select a record...'"
          :disabled="disabled"
          show-search
          allow-clear
          :filter-option="false"
          :options="entityRecordOptions"
          :loading="loadingEntityRecords"
          @search="searchEntityRecords"
          @change="handleEntityRecordSelect"
          class="w-full"
        />
        <div v-if="error && errorMessage" class="error_message mt-1">
          {{ errorMessage }}
        </div>
      </div>

      <CustomInput
        v-else
        ref="fallbackInputRef"
        v-model="modelValue"
        format="text"
        :placeholder="field.placeholder || 'Enter value'"
        :disabled="disabled"
        :isError="error"
        :errorMessage="errorMessage"
        @keydown.enter="handleEnterKey"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, reactive, onMounted, watch, nextTick } from "vue";
import dayjs from "dayjs";
import { useRouter } from "vue-router";
import { VueTelInput } from "vue-tel-input";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { fieldTypes } from "@/src/constants/formConstants.js";
import { meetingOutcomeOptions } from "@/src/constants/actionOptionsConstants.js";
import MeetingDispositionSelect from "@/src/components/meeting_components/MeetingDispositionSelect.vue";
import { getUserDisplayName } from "@/src/utils/utils.js";
import FormService from "@/src/api/FormService.js";
import DashboardService from "@/src/api/DashboardService.js";
import CRMService from "@/src/api/CRMService.js";
import { useFileUpload } from "@/src/composables/useFileUpload.js";
import { debounce } from "lodash";
import { transformCRMSearchResult } from "@/src/utils/crmFilterUtils.js";
import { fetchTagsForEntity } from "@/src/utils/dynamicFormUtils.js";
import {
  parseRelationRecords,
  getRecordUrl,
  getInitial,
  getAvatarColor,
} from "@/src/utils/dynamicFormUtils.js";
import store from "@/src/store/index.js";
import { getPrimaryAttributeSlug } from "@/src/utils/entityUtils.js";

const router = useRouter();

const defaultCountryCode = computed(() => store.getters["workspace/defaultCountryCode"]);

const props = defineProps({
  field: {
    type: Object,
    required: true,
  },
  modelValue: {
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: "",
  },
  onEnterKey: {
    type: Function,
    default: null,
  },
  onValueChange: {
    type: Function,
    default: null,
  },
  entityType: {
    type: String,
    default: null,
  },
  entityId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(["update:modelValue", "entity-record-selected"]);

const textInputRef = ref(null);
const numberInputRef = ref(null);
const urlInputRef = ref(null);
const fallbackInputRef = ref(null);
const selectRef = ref(null);
const multiSelectRef = ref(null);
const tagsSelectRef = ref(null);
const datePickerRef = ref(null);
const dateTimePickerRef = ref(null);
const textareaRef = ref(null);
const phoneInputRef = ref(null);
const userSelectRef = ref(null);
const uploadRef = ref(null);
const isTagsField = computed(() =>
  props.field.type === fieldTypes.TAGS || props.field.fieldName === 'tags'
);
const tagOptions = ref([]);

const meetingOutcomeOpts = meetingOutcomeOptions;

const generateTimeOptions = () => {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hour = h.toString().padStart(2, "0");
      const minute = m.toString().padStart(2, "0");
      const time24 = `${hour}:${minute}`;
      const period = h >= 12 ? "PM" : "AM";
      const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const label = `${displayHour}:${minute} ${period}`;
      options.push({ label, value: time24 });
    }
  }
  return options;
};

const timeOptions = generateTimeOptions();

const meetingLogValues = reactive({
  attendees: [],
  outcome: null,
  disposition: null,
  date: null,
  start_time: null,
  end_time: null,
  description: "",
  attachment_ids: [],
});

const {
  attachments: meetingAttachments,
  uploadFiles: uploadMeetingFiles,
  removeAttachment: removeMeetingAttachment,
  initializeAttachments: initializeMeetingAttachments,
} = useFileUpload();

const meetingAttachmentFileList = computed(() =>
  meetingAttachments.value.map((a) => ({
    uid: a.id,
    name: a.file_name || a.name || "File",
    status: a.isLoading ? "uploading" : "done",
    size: a.file_size,
  })),
);

const handleMeetingAttachmentChange = async (info) => {
  const { file } = info;
  if (file.status === "removed") {
    removeMeetingAttachment(file.uid || file.id);
    meetingLogValues.attachment_ids = meetingAttachments.value
      .filter((a) => !a.isLoading)
      .map((a) => a.id);
    return;
  }
  // a-upload with :before-upload="() => false" gives us raw File objects
  const rawFile = file.originFileObj || file;
  if (rawFile instanceof File) {
    await uploadMeetingFiles([rawFile]);
    meetingLogValues.attachment_ids = meetingAttachments.value
      .filter((a) => !a.isLoading)
      .map((a) => a.id);
  }
};

const attendeeContacts = ref([]);
const attendeeLeads = ref([]);
const associatedContacts = ref([]);
const associatedLeads = ref([]);
const allUsers = ref([]);
const attendeeUsers = ref([]);
const lockedAttendeeIds = ref(new Set());

const attendeeOptions = computed(() => {
  const associatedContactIds = new Set(associatedContacts.value.map(c => c.id));
  const associatedLeadIds = new Set(associatedLeads.value.map(l => l.id));

  return [
    {
      label: "Associated Contacts",
      options: associatedContacts.value
        .filter(c => getUserDisplayName(c) !== "")
        .map(c => ({
          value: c.id,
          label: getUserDisplayName(c) || c.email || String(c.id),
        })),
    },
    {
      label: "Associated Leads",
      options: associatedLeads.value
        .filter(l => getUserDisplayName(l) !== "")
        .map(l => ({
          value: l.id,
          label: getUserDisplayName(l) || l.email || String(l.id),
        })),
    },
    {
      label: "Users",
      options: attendeeUsers.value
        .filter(u => getUserDisplayName(u) !== "")
        .map(u => ({
          value: u.id,
          label: getUserDisplayName(u) || u.email,
        })),
    },
    {
      label: "Contacts",
      options: attendeeContacts.value
        .filter(c => getUserDisplayName(c) !== "" && !associatedContactIds.has(c.id))
        .map(c => ({
          value: c.id,
          label: getUserDisplayName(c) || c.email || String(c.id),
        })),
    },
    {
      label: "Leads",
      options: attendeeLeads.value
        .filter(l => getUserDisplayName(l) !== "" && !associatedLeadIds.has(l.id))
        .map(l => ({
          value: l.id,
          label: getUserDisplayName(l) || l.email || String(l.id),
        })),
    },
  ];
});

const normalizeEntityType = (type) => {
  if (!type) return "";
  const lower = type.toLowerCase();
  if (lower === "companies" || lower === "company") return "company";
  return lower.replace(/s$/, "");
};

const fetchAssociatedEntities = async () => {
  const entityType = normalizeEntityType(props.entityType);
  const entityId = props.entityId;
  if (!entityType || !entityId) return;

  try {
    if (entityType === "lead") {
      const response = await DashboardService.getLead(entityId);
      if (response?.success && response?.lead) {
        const prefixedId = "lead" + response.lead.id;
        associatedLeads.value = [{ ...response.lead, id: prefixedId }];
        if (!meetingLogValues.attendees.includes(prefixedId)) {
          meetingLogValues.attendees.push(prefixedId);
        }
        lockedAttendeeIds.value.add(prefixedId);
      }
    } else if (entityType === "contact") {
      const response = await DashboardService.getContact(entityId);
      if (response?.success && response?.contact) {
        const prefixedId = "contact" + response.contact.id;
        associatedContacts.value = [{ ...response.contact, id: prefixedId }];
        if (!meetingLogValues.attendees.includes(prefixedId)) {
          meetingLogValues.attendees.push(prefixedId);
        }
        lockedAttendeeIds.value.add(prefixedId);
      }
    } else if (entityType === "deal") {
      const [leadsRes, contactsRes] = await Promise.allSettled([
        DashboardService.getLeadsByDealID(entityId),
        DashboardService.fetchDealContacts(entityId),
      ]);
      if (leadsRes.status === "fulfilled" && leadsRes.value?.success && leadsRes.value?.leads) {
        associatedLeads.value = leadsRes.value.leads.map(l => ({ ...l, id: "lead" + l.id }));
      }
      if (contactsRes.status === "fulfilled" && contactsRes.value?.success && contactsRes.value?.contacts) {
        associatedContacts.value = contactsRes.value.contacts.map(c => ({ ...c, id: "contact" + c.id }));
      }
    } else if (entityType === "company") {
      const [leadsRes, contactsRes] = await Promise.allSettled([
        DashboardService.fetchCompanyLeads(entityId),
        DashboardService.fetchCompanyContacts(entityId),
      ]);
      if (leadsRes.status === "fulfilled" && leadsRes.value?.success && leadsRes.value?.leads) {
        associatedLeads.value = leadsRes.value.leads.map(l => ({ ...l, id: "lead" + l.id }));
      }
      if (contactsRes.status === "fulfilled" && contactsRes.value?.success && contactsRes.value?.contacts) {
        associatedContacts.value = contactsRes.value.contacts.map(c => ({ ...c, id: "contact" + c.id }));
      }
    }
  } catch (error) {
    console.error("Error fetching associated entities:", error);
  }
};

const fetchAttendeeContacts = async (searchTerm = "") => {
  try {
    const [contactsResult, leadsResult] = await Promise.allSettled([
      CRMService.searchAllFields({
        object_type: 'contacts',
        search_query: searchTerm,
        page: 1,
        page_size: 50,
      }),
      CRMService.searchAllFields({
        object_type: 'leads',
        search_query: searchTerm,
        page: 1,
        page_size: 50,
      }),
    ]);

    if (contactsResult.status === "fulfilled" && contactsResult.value.success) {
      attendeeContacts.value = (contactsResult.value.results || [])
        .map(item => transformCRMSearchResult(item, "contact"));
    }

    if (leadsResult.status === "fulfilled" && leadsResult.value.success) {
      attendeeLeads.value = (leadsResult.value.results || [])
        .map(item => transformCRMSearchResult(item, "lead"));
    }
  } catch (error) {
    console.error("Error fetching attendee contacts:", error);
  }
};

const fetchAttendeeUsers = async () => {
  try {
    const response = await DashboardService.fetchUsers();
    if (response.success) {
      const formattedUsers = response.users.map(user => ({
        id: "user" + user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
      }));
      allUsers.value = formattedUsers;
      attendeeUsers.value = formattedUsers;
    }
  } catch (error) {
    console.error("Error fetching users:", error);
  }
};

const searchAttendees = debounce(async (value = "") => {
  attendeeUsers.value = allUsers.value?.filter(user =>
    filterObjectOption(value, { label: getUserDisplayName(user) })
  ) || [];
  await fetchAttendeeContacts(value);
}, 300);

const handleAttendeesDropdownChange = async (visible) => {
  if (!visible) {
    await searchAttendees();
  }
};

const handleAttendeeDeselect = (value) => {
  if (lockedAttendeeIds.value.has(value)) {
    nextTick(() => {
      if (!meetingLogValues.attendees.includes(value)) {
        meetingLogValues.attendees.push(value);
      }
    });
  }
};

watch(
  meetingLogValues,
  (newVal) => {
    if (props.field.type === fieldTypes.MEETING_LOG) {
      emit("update:modelValue", { ...newVal });
    }
  },
  { deep: true }
);


const modelValue = computed({
  get: () => {
    if (isTagsField.value) {
      const val = props.modelValue;
      if (!val || (typeof val === 'string' && val.trim() === '')) return [];
      if (Array.isArray(val)) return val;
      return [];
    }
    return props.modelValue;
  },
  set: (value) => {
    emit("update:modelValue", value);
    if (props.onValueChange) {
      props.onValueChange();
    }
  },
});

const usersList = computed(() => store.getters["users/users"] || []);
const loadingUsers = computed(() => store.getters["users/loadingUsers"] || false);
const userOptions = computed(() => usersList.value.map(user => ({ value: user.id, label: user.name || user.email })));

const handleEnterKey = () => {
  if (props.onEnterKey) {
    props.onEnterKey();
  }
};

const filterObjectOption = (input, option) => {
  const label = option.label || option.children || option.value || '';
  return label.toString().toLowerCase().includes(input.toLowerCase());
};

const focus = () => {
  const refs = [
    textInputRef,
    numberInputRef,
    urlInputRef,
    fallbackInputRef,
  ];
  
  for (const refItem of refs) {
    if (refItem.value?.focus) {
      refItem.value.focus();
      return;
    }
  }
  
  if (selectRef.value?.$el) {
    selectRef.value.$el.querySelector('input')?.focus();
  } else if (multiSelectRef.value?.$el) {
    multiSelectRef.value.$el.querySelector('input')?.focus();
  } else if (datePickerRef.value?.$el) {
    datePickerRef.value.$el.querySelector('input')?.focus();
  } else if (dateTimePickerRef.value?.$el) {
    dateTimePickerRef.value.$el.querySelector('input')?.focus();
  } else if (textareaRef.value?.$el) {
    textareaRef.value.$el.querySelector('textarea')?.focus();
  } else if (phoneInputRef.value?.$el) {
    phoneInputRef.value.$el.querySelector('input')?.focus();
  } else if (userSelectRef.value?.$el) {
    userSelectRef.value.$el.querySelector('input')?.focus();
  } else if (tagsSelectRef.value?.$el) {
    tagsSelectRef.value.$el.querySelector('input')?.focus();
  }
};

const fetchUsers = async () => {
  try {
    if (!usersList.value || usersList.value.length === 0) {
      await store.dispatch("users/fetchUsers");
    }
  } catch (error) {
    console.error("Failed to fetch users:", error);
  }
};

const fetchTags = async () => {
  const entitySlug = props.field.attributeData?.object_slug;
  if (!entitySlug) return;
  try {
    const tags = await fetchTagsForEntity(entitySlug);
    tagOptions.value = tags;
  } catch (error) {
    console.error("Failed to fetch tags:", error);
  }
};

const getImageStyles = (field) => {
  const styles = {};
  if (field.imageWidth === 'full') styles.width = '100%';
  else if (field.imageWidth === 'half') styles.width = '50%';
  else if (field.imageWidth === 'custom' && field.customWidth) styles.width = `${field.customWidth}px`;
  return styles;
};

const parsedRelationRecords = computed(() =>
  parseRelationRecords(props.modelValue),
);

const navigateToRecord = (record) => {
  if (!record) return;
  router.push(getRecordUrl(record, store));
};

// --- Entity field state and methods ---
const entityRecordOptions = ref([]);
const allEntityRecords = ref([]);
const loadingEntityRecords = ref(false);
const entityPrimaryAttrSlug = ref("");

const getEntityRecordDisplayName = (record) => {
  const relatedObj = record.related_object || record;
  const attrs = relatedObj.attributes || {};
  if (entityPrimaryAttrSlug.value) {
    const primaryVal = attrs[entityPrimaryAttrSlug.value];
    if (primaryVal && typeof primaryVal === "object") {
      // Person-style name: { first_name, last_name, full_name }
      if (primaryVal.full_name) return primaryVal.full_name;
      if (primaryVal.first_name || primaryVal.last_name) {
        return [primaryVal.first_name, primaryVal.last_name].filter(Boolean).join(" ");
      }
      // Standard wrapper: { value: "..." }
      if (primaryVal.value != null && String(primaryVal.value).trim()) return String(primaryVal.value);
    } else if (primaryVal && String(primaryVal).trim()) {
      return String(primaryVal);
    }
  }
  // Fallback: try common name/title attributes directly
  const nameAttr = attrs.name;
  if (nameAttr && typeof nameAttr === "object") {
    if (nameAttr.full_name) return nameAttr.full_name;
    if (nameAttr.first_name || nameAttr.last_name) {
      return [nameAttr.first_name, nameAttr.last_name].filter(Boolean).join(" ");
    }
    if (typeof nameAttr.value === "string" && nameAttr.value.trim()) return nameAttr.value;
  }
  if (attrs.title?.value && String(attrs.title.value).trim()) return String(attrs.title.value);
  return relatedObj.display_name || (relatedObj.id ? String(relatedObj.id) : "Unnamed Record");
};

const buildEntityOptions = (records) =>
  records.map((record) => ({
    value: String((record.related_object || record).id),
    label: getEntityRecordDisplayName(record),
  }));

const fetchEntityRecords = async () => {
  if (props.field.type !== fieldTypes.ENTITY) return;
  if (!props.field.entityConfig?.objectSlug) return;
  if (!props.entityType || !props.entityId) return;

  loadingEntityRecords.value = true;
  try {
    const objectSlug = props.field.entityConfig.objectSlug;
    entityPrimaryAttrSlug.value = await getPrimaryAttributeSlug(objectSlug);

    const result = await CRMService.getRelationshipConnections({
      object_type: props.entityType,
      object_id: String(props.entityId),
      related_object_type: objectSlug,
      page: 1,
      page_size: 50,
    });

    const records = result?.relationships || result?.data || [];
    // Deduplicate by record ID to avoid duplicate dropdown entries from multiple sequences
    const seen = new Set();
    const uniqueRecords = records.filter((record) => {
      const relatedObj = record.related_object || record;
      const id = String(relatedObj.id);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    allEntityRecords.value = uniqueRecords;
    entityRecordOptions.value = buildEntityOptions(uniqueRecords);
  } catch (error) {
    console.error("Error fetching entity records:", error);
    entityRecordOptions.value = [];
    allEntityRecords.value = [];
  } finally {
    loadingEntityRecords.value = false;
  }
};

const searchEntityRecords = debounce((query) => {
  if (!query) {
    entityRecordOptions.value = buildEntityOptions(allEntityRecords.value);
    return;
  }
  const lowerQuery = query.toLowerCase();
  entityRecordOptions.value = buildEntityOptions(allEntityRecords.value)
    .filter((opt) => String(opt.label).toLowerCase().includes(lowerQuery));
}, 300);

const handleEntityRecordSelect = (recordId) => {
  if (!recordId) return;
  const record = allEntityRecords.value.find((r) => {
    const relatedObj = r.related_object || r;
    return String(relatedObj.id) === String(recordId);
  });
  if (record) {
    const relatedObj = record.related_object || record;
    emit("entity-record-selected", {
      objectSlug: props.field.entityConfig?.objectSlug,
      recordId: String(relatedObj.id),
      recordData: relatedObj,
    });
  }
};

const fetchAttachmentsByIds = async (ids) => {
  if (!ids || !ids.length) return [];
  try {
    const results = await Promise.allSettled(
      ids.map((id) => DashboardService.fetchAttachmentById(id)),
    );
    return results
      .filter((r) => r.status === "fulfilled" && r.value?.success)
      .map((r) => r.value.file);
  } catch {
    return [];
  }
};

const applyMeetingLogValues = async (incoming) => {
  if (!incoming || typeof incoming !== 'object') return;
  const normalized = { ...incoming };
  if (normalized.date && typeof normalized.date === 'string') {
    normalized.date = dayjs(normalized.date);
  }
  // Initialize attachment composable from existing attachment data
  if (normalized.attachments && Array.isArray(normalized.attachments)) {
    initializeMeetingAttachments(normalized.attachments);
    normalized.attachment_ids = normalized.attachments.map((a) => a.id);
    delete normalized.attachments;
  } else if (normalized.attachment_ids && Array.isArray(normalized.attachment_ids) && normalized.attachment_ids.length > 0) {
    // Edit mode: only IDs stored, fetch full attachment details
    const attachments = await fetchAttachmentsByIds(normalized.attachment_ids);
    if (attachments.length > 0) {
      initializeMeetingAttachments(attachments);
    }
  }
  Object.assign(meetingLogValues, normalized);
};

watch(
  () => props.modelValue,
  (incoming) => {
    if (props.field.type === fieldTypes.MEETING_LOG) {
      applyMeetingLogValues(incoming);
    }
  },
  { once: true },
);

onMounted(() => {
  store.dispatch("workspace/fetchDefaultCountryCode");
  if (props.field.type === fieldTypes.USER) {
    fetchUsers();
  }
  if (isTagsField.value) {
    fetchTags();
  }
  if (props.field.type === fieldTypes.MEETING_LOG) {
    fetchAttendeeUsers();
    fetchAssociatedEntities();
    fetchAttendeeContacts();
    applyMeetingLogValues(props.modelValue);
  }
  if (props.field.type === fieldTypes.ENTITY) {
    fetchEntityRecords();
  }
});

defineExpose({ focus });
</script>

<style scoped>
.form_field {
  margin-bottom: 1rem;
}

.whitespace_pre_wrap {
  white-space: pre-wrap;
  word-wrap: break-word;
}

.image_container {
  max-width: 100%;
}

.error_message {
  color: var(--error-color);
  font-size: 12px;
  line-height: 14px;
}

.field-error :deep(.ant-select-selector),
.field-error :deep(.ant-picker),
.field-error :deep(.ant-input-number),
.field-error :deep(.ant-input-number-input),
.field-error :deep(.ant-textarea),
.field-error :deep(.vue-tel-input .vti__input) {
  border-color: var(--error-color) !important;
}

.read-only-field {
  opacity: 0.9;
  cursor: not-allowed;
}

.readonly-value-display {
  padding: 8px 12px;
  border-radius: 6px;
  background: var(--white-opacity-4);
  min-height: 38px;
  display: flex;
  align-items: center;
}

.relation-record-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 9999px;
  background: var(--secondary-button-bg);
  border: 1px solid var(--border-color);
  cursor: pointer;
  transition: background-color 0.2s ease;
  max-width: 200px;
}

.relation-record-chip:hover {
  background: var(--white-opacity-8);
}

.relation-record-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  min-height: 20px;
  width: 20px;
  height: 20px;
  font-size: 10px;
  border-radius: 50%;
  color: white;
  font-weight: 500;
}

.relation-record-name {
  font-size: 14px;
  color: var(--text-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
